import { IconChevronLeft, IconChevronRight, IconPrinter } from '@tabler/icons-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Banner, Dropdown, Letterhead, Spinner } from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import { navigate } from '@/lib/router';
import type { Clinic, Protocol } from '@/types/api';
import { missingProtocolFields } from '@/utils/patient';
import { useAuth } from '../auth';
import { formatDate } from '../chart/components';
import { useDoctor } from '../doctors';
import { useClinicSettings, useLogoUrl } from '../settings';
import { protocolApi } from './api';
import { ProtocolTable } from './ProtocolTable';

/** Today (or `days` from `day`) as YYYY-MM-DD, in the device's (the clinic's) time. */
function dayString(base?: string, days = 0): string {
  const d = base ? new Date(`${base}T12:00:00`) : new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The day's protocol on the clinic's letterhead, A4 landscape. */
function ProtocolPaper(p: {
  protocol: Protocol;
  doctor: string | null;
  clinic: Clinic | null;
  logoUrl: string | null;
}) {
  const { t } = useTranslation();
  return (
    <article className="offer-paper protocol-paper">
      <Letterhead clinic={p.clinic} logoUrl={p.logoUrl} compact />
      <h1 className="offer-paper__title">
        {t('protocol.paperTitle', { date: formatDate(`${p.protocol.day}T12:00:00`) })}
        {p.doctor && <span className="protocol-paper__doctor"> · {p.doctor}</span>}
      </h1>
      <ProtocolTable rows={p.protocol.rows} />
    </article>
  );
}

/**
 * The daily patient protocol (Protokol bolesnika, #23): every visit of a day,
 * of one doctor or the whole clinic, as the law asks it kept, ready to print.
 */
export function ProtocolPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { all: doctors, doctorId } = useDoctor();
  const [day, setDay] = useState(() => dayString());
  const [dentistId, setDentistId] = useState<string | null>(doctorId);
  const protocol = useResource(() => protocolApi.day({ day, dentistId }), `${day}|${dentistId}`);
  const clinic = useClinicSettings(user?.clinic?.id ?? '');
  const logoUrl = useLogoUrl(clinic?.logoVersion ?? null);
  const doctorName = doctors.find((d) => d.id === dentistId)?.name ?? null;
  const incomplete =
    protocol.data?.rows.filter((r) => missingProtocolFields(r.patient).length) ?? [];

  return (
    <div className="stack protocol">
      <div className="page-head no-print">
        <div className="page-head__title">
          <h1>{t('protocol.title')}</h1>
        </div>
        <button
          type="button"
          className="btn btn--dark"
          disabled={!protocol.data?.rows.length}
          onClick={() => window.print()}
        >
          <IconPrinter size={18} /> {t('protocol.print')}
        </button>
      </div>

      <div className="filters no-print">
        <div className="day-picker">
          <button
            type="button"
            className="icon-btn"
            onClick={() => setDay(dayString(day, -1))}
            aria-label={t('protocol.previousDay')}
          >
            <IconChevronLeft size={20} />
          </button>
          <input
            className="input"
            type="date"
            aria-label={t('protocol.day')}
            value={day}
            onChange={(e) => e.target.value && setDay(e.target.value)}
          />
          <button
            type="button"
            className="icon-btn"
            onClick={() => setDay(dayString(day, 1))}
            aria-label={t('protocol.nextDay')}
          >
            <IconChevronRight size={20} />
          </button>
        </div>
        <Dropdown
          ariaLabel={t('calendar.filter')}
          value={dentistId ?? ''}
          options={[
            { value: '', label: t('reports.allDoctors') },
            ...doctors.map((d) => ({ value: d.id, label: d.name })),
          ]}
          onChange={(id) => setDentistId(id || null)}
        />
      </div>

      {protocol.error ? (
        <Banner
          tone="error"
          action={
            <button type="button" className="btn btn--sm" onClick={() => protocol.reload()}>
              {t('common.retry')}
            </button>
          }
        >
          {errorMessage(protocol.error)}
        </Banner>
      ) : !protocol.data ? (
        <Spinner />
      ) : protocol.data.rows.length === 0 ? (
        <p className="muted">{t('protocol.empty')}</p>
      ) : (
        <>
          {incomplete.length > 0 && (
            <Banner tone="warning">
              {t('protocol.incomplete', { count: incomplete.length })}{' '}
              {incomplete.map((r, i) => (
                <span key={r.visitId}>
                  {i > 0 && ', '}
                  <button
                    type="button"
                    className="link"
                    onClick={() => navigate({ name: 'patient', id: r.patient.id })}
                  >
                    {r.patient.lastName} {r.patient.firstName}
                  </button>
                </span>
              ))}
            </Banner>
          )}
          <section className="card protocol__card">
            <ProtocolTable
              rows={protocol.data.rows}
              onPatient={(id) => navigate({ name: 'patient', id })}
            />
          </section>
          {createPortal(
            <div className="print-root">
              <ProtocolPaper
                protocol={protocol.data}
                doctor={doctorName}
                clinic={clinic}
                logoUrl={logoUrl}
              />
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
}
